import React, { createContext, useState, useEffect, useContext, useMemo } from 'react';

const EnvironmentContext = createContext();

export function useEnvironment() {
    return useContext(EnvironmentContext);
}

export function EnvironmentProvider({ children }) {
    // Get the current mode from Vite (local, development, staging, production)
    const [environment, setEnvironment] = useState(import.meta.env.MODE || 'production');

    // Load saved environment on mount, but prefer Vite mode
    useEffect(() => {
        const viteMode = import.meta.env.MODE;
        if (viteMode) {
            setEnvironment(viteMode);
        } else {
            const savedEnv = localStorage.getItem('appEnvironment');
            if (savedEnv) {
                setEnvironment(savedEnv);
            }
        }
    }, []);

    // Memoize API endpoints to prevent unnecessary re-renders
    const apiEndpoints = useMemo(() => ({
        upload: import.meta.env.VITE_UPLOAD_API_URL || '',
        ask: import.meta.env.VITE_ASK_API_URL || '',
        approve: import.meta.env.VITE_UPLOAD_APPROVE_API_URL || '',
        base: import.meta.env.VITE_API_URL || '',
        icloud: import.meta.env.VITE_ICLOUD_API_URL || '',
    }), [environment]);

    // Memoize calendar API endpoints to prevent unnecessary re-renders
    const calendarApiEndpoints = useMemo(() => ({
        icloud: import.meta.env.VITE_ICLOUD_API_URL || '',
        google: import.meta.env.VITE_GOOGLE_CALENDAR_API_URL || '',
    }), [environment]);

    // Memoize Firebase config to prevent unnecessary re-renders
    const fireBaseConfig = useMemo(() => ({
        apiKey: import.meta.env.VITE_FIREBASE_API_KEY || '',
        authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || '',
        projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || '',
        storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || '',
        messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '',
        appId: import.meta.env.VITE_FIREBASE_APP_ID || '',
        databaseID: import.meta.env.VITE_FIREBASE_DATABASE_ID || ''
    }), [environment]);

    // Function to change environment (mainly for manual override)
    const changeEnvironment = (newEnv) => {
        setEnvironment(newEnv);
        localStorage.setItem('appEnvironment', newEnv);
    };

    // Helper functions to check environment
    const isLocal = environment === 'local';
    const isDevelopment = environment === 'development';
    const isStaging = environment === 'staging';
    const isProduction = environment === 'production';

    // Get environment display name
    const getEnvironmentDisplayName = () => {
        switch (environment) {
            case 'local':
                return 'Local';
            case 'development':
                return 'Development';
            case 'staging':
                return 'Staging';
            case 'production':
                return 'Production';
            default:
                return 'Unknown';
        }
    };

    // Get environment color for UI indication
    const getEnvironmentColor = () => {
        switch (environment) {
            case 'local':
                return '#28a745'; // Green
            case 'development':
                return '#007bff'; // Blue
            case 'staging':
                return '#ffc107'; // Yellow
            case 'production':
                return '#dc3545'; // Red
            default:
                return '#6c757d'; // Gray
        }
    };

    const value = useMemo(() => ({
        environment,
        changeEnvironment,
        isStaging,
        apiEndpoints,
        calendarApiEndpoints,
        fireBaseConfig,
        getEnvironmentDisplayName,
        getEnvironmentColor,
        // Legacy support
        isLocal: isLocal,
        isDevelopment: isDevelopment || isLocal,
        isProduction: isProduction || isStaging
    }), [environment, apiEndpoints, calendarApiEndpoints, fireBaseConfig, isStaging, isLocal, isDevelopment, isProduction]);

    return (
        <EnvironmentContext.Provider value={value}>
            {children}
        </EnvironmentContext.Provider>
    );
}